function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    // In RTL, item 1 sits visually to the left of item 0 (DOM order is unchanged,
    // only the row direction reverses), so this difference comes out negative -
    // normalize it to a magnitude since everywhere else treats it as a distance.
    this.sliderItemOffset = Math.abs(
      this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft
    );
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(Math.abs(this.slider.scrollLeft) / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    // Compares rendered positions rather than doing scrollLeft arithmetic, since
    // scrollLeft's sign/zero-point convention for RTL content differs across browsers.
    const sliderRect = this.slider.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    return (
      Math.round(elementRect.left) >= Math.round(sliderRect.left) &&
      Math.round(elementRect.right) <= Math.round(sliderRect.right) - offset
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    // In RTL, scrollLeft goes negative to advance instead of positive - flip
    // which direction "next"/"previous" move in (see autoRotateSlides()).
    const isRTL = getComputedStyle(this.slider).direction === 'rtl';
    const isNext = event.currentTarget.name === 'next';
    const goNegative = isNext === isRTL;
    this.slideScrollPosition = goNegative
      ? this.slider.scrollLeft - step * this.sliderItemOffset
      : this.slider.scrollLeft + step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      // Wrapping to the last slide sits at a large negative scrollLeft in RTL
      // instead of a large positive one (see autoRotateSlides()).
      const isRTL = getComputedStyle(this.slider).direction === 'rtl';
      const wrapDistance = this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
      this.slideScrollPosition = isRTL
        ? this.slider.scrollLeft - wrapDistance
        : this.slider.scrollLeft + wrapDistance;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout (() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    // In RTL, scrollLeft's valid range is 0 down to a negative number (0 is
    // already the max), so adding a positive offset to advance just clamps
    // back to 0 - the step has to go negative there instead.
    const isRTL = getComputedStyle(this.slider).direction === 'rtl';
    const step = isRTL ? -this.sliderItemOffset : this.sliderItemOffset;

    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + step;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
        window.gtmPushViewItemList?.(this, this.dataset.sectionId, this.dataset.sectionName);
        document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  getCartQuantityForLine(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
  }

  startCartLinesUpdate(items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
      const nextQuantity = parseInt(quantity, 10);
      const currentQuantity = this.getCartQuantityForLine(variantId);

      if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

      const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: variantId, quantity: nextQuantity };
      } else {
        const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
        // No AJAX line key on the row — likely cached HTML rendered before this
        // attribute landed. Skip rather than emit an event with id: ''.
        if (!lineKey) return groups;
        line = { id: lineKey, quantity: nextQuantity };
      }

      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((deferred) => deferred.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((deferred) => deferred.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;

    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


// custom js for hero banner

if (!customElements.get("hero-video")) {
  class HeroVideo extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector(".hero-swiper");

      this.handleResize = this.handleResize.bind(this);

      this.initSwiper();
      this.bindControls();

      window.addEventListener("resize", this.handleResize);
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this.handleResize);

      this.querySelectorAll(".playPauseBtn").forEach((btn) => {
        btn.removeEventListener("click", btn._playHandler);
      });

      this.querySelectorAll(".muteBtn").forEach((btn) => {
        btn.removeEventListener("click", btn._muteHandler);
      });

      this.querySelectorAll("video").forEach((video) => {
        if (video._endedHandler) {
          video.removeEventListener("ended", video._endedHandler);
        }
      });

      this.swiper?.destroy(true, true);
    }

    initSwiper() {
      if (!this.swiperEl) return;

      this.swiper = new Swiper(this.swiperEl, {
        loop: this.dataset.loop === "true",

        speed: 600,

        autoplay:
          this.dataset.autoplay === "true"
            ? {
                delay: Number(this.dataset.speed) || 5000,
                disableOnInteraction: false,
              }
            : false,

        pagination: {
          el: this.querySelector(".swiper-pagination"),
          clickable: true,
        },

        navigation: {
          nextEl: this.querySelector(".hero-swiper-next"),
          prevEl: this.querySelector(".hero-swiper-prev"),
        },

        on: {
          init: (swiperInstance) => {
            this.swiper = swiperInstance;
            this.updateVideos();
          },

          slideChangeTransitionEnd: () => {
            this.updateVideos();
          },
        },
      });
    }

    bindControls() {
      this.querySelectorAll(".playPauseBtn").forEach((btn) => {
        const handler = () => this.togglePlay(btn);

        btn._playHandler = handler;
        btn.addEventListener("click", handler);
      });

      this.querySelectorAll(".muteBtn").forEach((btn) => {
        const handler = () => this.toggleMute(btn);

        btn._muteHandler = handler;
        btn.addEventListener("click", handler);
      });
    }

    getActiveVideo(slide) {
      if (!slide) return null;

      return window.innerWidth <= 767
        ? slide.querySelector(".mobile-video")
        : slide.querySelector(".desktop-video");
    }

    slideHasVideo(slide) {
      if (!slide) return false;
      return !!(
        slide.querySelector(".desktop-video") ||
        slide.querySelector(".mobile-video")
      );
    }

    bindVideoAdvance(video) {
      if (!video) return;

      if (video._endedHandler) {
        video.removeEventListener("ended", video._endedHandler);
      }

      const handler = () => {
        if (!this.swiper) return;
        this.swiper.slideNext();
      };

      video._endedHandler = handler;
      video.addEventListener("ended", handler);
    }

    updateVideos() {
      const slides = this.querySelectorAll(".swiper-slide");
      const autoplayEnabled = this.dataset.autoplay === "true";

      slides.forEach((slide) => {
        const desktopVideo = slide.querySelector(".desktop-video");
        const mobileVideo = slide.querySelector(".mobile-video");
        const isActive = slide.classList.contains("swiper-slide-active");

        [desktopVideo, mobileVideo].forEach((video) => {
          if (!video) return;

          if (isActive) {
            video.play().catch(() => {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });

        this.updateButtons(slide);
      });

      if (autoplayEnabled && this.swiper?.autoplay) {
        const activeSlide = this.querySelector(".swiper-slide-active");
        const activeVideo = this.getActiveVideo(activeSlide);

        if (activeVideo) {
          this.swiper.autoplay.stop();
          this.bindVideoAdvance(activeVideo);
        } else {
          this.swiper.autoplay.start();
        }
      }
    }

    togglePlay(button) {
      const slide = button.closest(".swiper-slide");
      if (!slide) return;

      const video = this.getActiveVideo(slide);
      if (!video) return;

      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }

      const desktopVideo = slide.querySelector(".desktop-video");
      const mobileVideo = slide.querySelector(".mobile-video");

      [desktopVideo, mobileVideo].forEach((v) => {
        if (!v || v === video) return;

        if (video.paused) {
          v.pause();
        } else {
          v.play().catch(() => {});
        }
      });

      this.updateButtons(slide);
    }

    toggleMute(button) {
      const slide = button.closest(".swiper-slide");
      if (!slide) return;

      const video = this.getActiveVideo(slide);
      if (!video) return;

      const muted = !video.muted;

      slide.querySelectorAll("video").forEach((v) => {
        v.muted = muted;
      });

      this.updateButtons(slide);
    }

    updateButtons(slide) {
      const video = this.getActiveVideo(slide);
      if (!video) return;

      const playBtn = slide.querySelector(".playPauseBtn");
      const muteBtn = slide.querySelector(".muteBtn");

      playBtn?.classList.toggle("pause", video.paused);
      muteBtn?.classList.toggle("muted", video.muted);
    }

    handleResize() {
      this.updateVideos();
    }
  }

  customElements.define("hero-video", HeroVideo);
}

if (!customElements.get("media-with-text-video")) {
  class MediaWithTextVideo extends HTMLElement {
    connectedCallback() {
      this.playBtn = this.querySelector(".playPauseBtn");
      this.muteBtn = this.querySelector(".muteBtn");

      this.playHandler = () => this.togglePlay();
      this.muteHandler = () => this.toggleMute();

      this.playBtn?.addEventListener("click", this.playHandler);
      this.muteBtn?.addEventListener("click", this.muteHandler);
    }

    disconnectedCallback() {
      this.playBtn?.removeEventListener("click", this.playHandler);
      this.muteBtn?.removeEventListener("click", this.muteHandler);
    }

    getActiveVideo() {
      return window.innerWidth <= 767
        ? this.querySelector(".mobile-video") || this.querySelector(".desktop-video")
        : this.querySelector(".desktop-video") || this.querySelector(".mobile-video");
    }

    togglePlay() {
      const video = this.getActiveVideo();
      if (!video) return;

      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }

      this.updateButtons();
    }

    toggleMute() {
      const video = this.getActiveVideo();
      if (!video) return;

      const muted = !video.muted;
      this.querySelectorAll("video").forEach((v) => {
        v.muted = muted;
      });

      this.updateButtons();
    }

    updateButtons() {
      const video = this.getActiveVideo();
      if (!video) return;

      this.playBtn?.classList.toggle("pause", video.paused);
      this.muteBtn?.classList.toggle("muted", video.muted);
    }
  }

  customElements.define("media-with-text-video", MediaWithTextVideo);
}


// custom js for hero banner

if (!customElements.get("card-product")) {
  class CardProduct extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector(".variant-select");
      this.variantInput = this.querySelector('product-form input[name="id"]');
      this.addToCartBtn = this.querySelector('product-form button[type="submit"]');
      this.buttonLabel = this.addToCartBtn?.querySelector("span");

      this.priceWrapper = this.querySelector(".price");
      this.regularContainer = this.priceWrapper?.querySelector(".price__regular");
      this.saleContainer = this.priceWrapper?.querySelector(".price__sale");

      this.regularPrice = this.regularContainer?.querySelector(".price-item--regular");
      this.salePrice = this.saleContainer?.querySelector(".price-item--sale");
      this.comparePrice = this.saleContainer?.querySelector(".price-item--regular");

      this.description = this.querySelector(".card__description");
      this.hasDescription = this.dataset.des === "true";

      this.imageFirst = this.querySelector(".card__media .media img:first-of-type");
      this.imageSecond = this.querySelector(".card__media .media img:last-of-type");

      this.hasSwiper = this.dataset.swiper === "true";
      this.swiperEl = this.querySelector(".card__swiper");
      this.swiper = null;

      if (this.hasSwiper) {
        this.initSwiper();
      }

      if (!this.select || !this.variantInput || !this.addToCartBtn) return;

      this.onVariantChange = this.onVariantChange.bind(this);

      this.select.addEventListener("change", this.onVariantChange);
    }

    disconnectedCallback() {
      this.select?.removeEventListener("change", this.onVariantChange);
    }

    onVariantChange() {
      const option = this.select.selectedOptions[0];
      if (!option) return;

      this.variantInput.value = option.value;

      this.updateAvailability(option);
      this.updatePrice(option);
      this.updateDescription(option);
      this.updateImage(option);
      this.updateGtmPayload(option);
    }

    updateGtmPayload(option) {
      const gtmPayloadInput = this.closest('.product-card-wrapper')?.querySelector('.gtmPayload');
      if (!gtmPayloadInput) return;

      try {
        const payload = JSON.parse(gtmPayloadInput.value);
        const item = payload.ecommerce?.items?.[0];
        if (!item) return;

        const price = Number(option.dataset.gtmPrice);

        item.item_variant = option.dataset.gtmItemVariant || '';
        item.item_variant_id = option.dataset.gtmItemVariantId || '';
        item.price = price;
        item.product_size = option.dataset.gtmProductSize || '';
        item.sale_status = option.dataset.gtmSaleStatus;
        item.stock_status = option.dataset.gtmStockStatus;
        item.discount = option.dataset.gtmDiscount;
        payload.ecommerce.value = price;

        gtmPayloadInput.value = JSON.stringify(payload);
      } catch (e) {
        // malformed/missing payload - nothing to update
      }
    }

    initSwiper() {
      if (!this.swiperEl || typeof Swiper === "undefined") return;

      if (this.swiperEl.swiper) {
        this.swiper = this.swiperEl.swiper;
        return;
      }

      this.swiper = new Swiper(this.swiperEl, {
        slidesPerView: 1,

        pagination: {
          el: this.swiperEl.querySelector(".card-pagination"),
          type: "progressbar",
        },

        navigation: {
          nextEl: this.querySelector(".card-btn-next"),
          prevEl: this.querySelector(".card-btn-prev"),
        },
      });
    }

    updateAvailability(option) {
      const available = option.dataset.available === "true";

      this.addToCartBtn.disabled = !available;

      if (this.buttonLabel) {
        this.buttonLabel.textContent = available
          ? window.themeCore.translations.product.add_to_cart
          : window.themeCore.translations.product.sold_out;
      }
    }

    updatePrice(option) {
      if (!this.priceWrapper) return;

      const price = option.dataset.price;
      const comparePrice = option.dataset.comparePrice;

      const onSale =
        comparePrice &&
        comparePrice !== "" &&
        comparePrice !== price;

      this.priceWrapper.classList.toggle("price--on-sale", onSale);
      this.priceWrapper.classList.toggle("show-sale-price", onSale);

      if (onSale) {
        if (this.salePrice) this.salePrice.textContent = price;
        if (this.comparePrice) this.comparePrice.textContent = comparePrice;

        if (this.regularContainer) this.regularContainer.style.display = "none";
        if (this.saleContainer) this.saleContainer.style.display = "block";
      } else {
        if (this.regularPrice) this.regularPrice.textContent = price;

        if (this.regularContainer) this.regularContainer.style.display = "block";
        if (this.saleContainer) this.saleContainer.style.display = "none";
      }
    }

    updateDescription(option) {
      if (!this.description) return;

      if (this.hasDescription) {
        this.description.innerHTML = option.dataset.description;
      }
    }

    updateImage(option) {
      // Swiper version
      if (this.hasSwiper && this.swiper) {
        const mediaId = option.dataset.mediaId;
        if (!mediaId) return;

        const index = [...this.swiper.slides].findIndex(
          (slide) => slide.dataset.mediaId === mediaId
        );

        if (index > -1) {
          this.swiper.slideTo(index);
        }

        return;
      }

      // Existing image swap logic
      if (!this.imageFirst) return;

      const image = option.dataset.image;
      const firstSrc = this.imageFirst.src;

      this.imageFirst.removeAttribute("srcset");

      if (image) {
        this.imageFirst.src = image;

        if (this.imageSecond) {
          this.imageSecond.removeAttribute("srcset");
          this.imageSecond.src = firstSrc;
        }
      }
    }

  }

  customElements.define("card-product", CardProduct);
}


// mega menu details open

document.addEventListener('DOMContentLoaded', function () {
  if(window.innerWidth > 1199){

    const detailsElementsParent = document.querySelector('.header__inline-menu .list-menu--inline');
    const detailsElements = document.querySelectorAll('.mega-menu');
  
    const OPEN_DELAY = 200;
    let hasOpenedOnce = false;
  
    detailsElementsParent.addEventListener('mouseenter', () => {
      detailsElementsParent.classList.add('open');
    });
  
    detailsElementsParent.addEventListener('mouseleave', () => {
      detailsElementsParent.classList.remove('open');
      hasOpenedOnce = false;
    });
  
    detailsElements.forEach(details => {
      let openTimeout;
  
      details.addEventListener('mouseenter', () => {
        if (hasOpenedOnce) {
          details.setAttribute('open', 'true');
        } else {
          openTimeout = setTimeout(() => {
            if(document.body.classList.contains('en')){
              document.querySelector('.header__search')?.close(false);
            }
            details.setAttribute('open', 'true');
            hasOpenedOnce = true;
          }, OPEN_DELAY);
        }
      });
  
      details.addEventListener('mouseleave', () => {
        clearTimeout(openTimeout);
        details.removeAttribute('open');
      });
    });
  }
});


// country popup footer

document.querySelectorAll('.location-dropdown').forEach((dropdown) => {
  const selected = dropdown.querySelector('.location-dropdown__selected');
  const items = dropdown.querySelectorAll('.location-dropdown__list li');

  if (!selected || !items.length) return;

  items.forEach((item) => {
    item.addEventListener('click', () => {
      selected.textContent = item.textContent.trim();
      dropdown.removeAttribute('open');

      const { url } = item.dataset;
      if (url) {
        window.location.href = url;
      }
    });
  });
});

document.addEventListener('click', (event) => {
  document.querySelectorAll('.location-dropdown[open]').forEach((details) => {
    if (!details.contains(event.target)) {
      details.removeAttribute('open');
    }
  });
});



// Infinity scroll on plp and search
if (document.body.classList.contains('collection') || document.body.classList.contains('search')) {
  function infinite() {
    const mainGrid = document.querySelector('.main-product-grid');
    const infiniteScrollElement = document.querySelector('.infinite__scroll--button-wrap');
    let isLoading = false;

    if (infiniteScrollElement && mainGrid) {
      const Ajaxbutton = infiniteScrollElement.querySelector("#ajaxcall");
      let nextPageURL = Ajaxbutton.getAttribute('href');
      const backToTop = document.querySelector(".no-more-cstm");

      const loadMoreContent = () => {
        if (isLoading || !nextPageURL) return;
        isLoading = true;
        Ajaxbutton.classList.add('loading');
        backToTop.classList.add("hide-backto-top");

        const loadedPage = Number(new URL(nextPageURL, window.location.href).searchParams.get('page')) || 2;

        fetch(nextPageURL)
          .then((response) => {
            if (!response.ok) {
              const error = new Error(response.status);
              throw error;
            }
            return response.text();
          })
          .then((text) => {
            let parsedHTML = new DOMParser().parseFromString(text, 'text/html');
            let productGrid = parsedHTML.querySelector('.main-product-grid');
            let nextUrl = parsedHTML.querySelector('#ajaxcall');

            if (!nextUrl) {
              nextPageURL = null;
              Ajaxbutton.classList.remove('loading');
              Ajaxbutton.style.display = "none";
              backToTop.style.display = "block";
            } else {
              backToTop.style.display = "none";
              nextPageURL = nextUrl.href;
              Ajaxbutton.setAttribute('href', nextPageURL);
              Ajaxbutton.classList.remove('loading');
            }

            if (productGrid) {
              const previousCardCount = mainGrid.querySelectorAll('.product-card-wrapper').length;
              mainGrid.insertAdjacentHTML('beforeend', productGrid.innerHTML);
              document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

              // Re-run view_item_list for just the newly rendered cards, keeping the
              // same item_list_id/item_list_name and continuing the index sequence
              // from where the previous page left off (see gtmLoadmore in snippets/gtm.liquid).
              if (typeof window.gtmLoadmore === 'function') {
                const newCards = Array.from(mainGrid.querySelectorAll('.product-card-wrapper')).slice(previousCardCount);
                const perPage = Number(mainGrid.dataset.perPage) || newCards.length;
                if (newCards.length) window.gtmLoadmore(newCards, loadedPage, perPage);
              }
            }

            isLoading = false;
            backtoTop();
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
            isLoading = false;
            Ajaxbutton.classList.remove('loading');
          });
      };

      // Trigger on click instead of on scroll
      Ajaxbutton.addEventListener('click', function (e) {
        e.preventDefault();
        loadMoreContent();
      });
    }
  }
  infinite();
}
function backtoTop() {
  document.querySelectorAll(".no-more-cstm").forEach(backtotop => {
    backtotop.addEventListener("click", function () {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      })
    })
  })
}
backtoTop();



document.querySelectorAll("[data-section-slider]").forEach((slider) => {
  const section = slider.closest(".shopify-section");

  if (!section) return;

  const nextBtn = section.querySelector(".swiper-btn-next");
  const prevBtn = section.querySelector(".swiper-btn-prev");
  const pagination = section.querySelector(".swiper-pagination:not(.card-pagination)");

  const spaceMobile = slider.dataset.spaceMobile !== undefined ? Number(slider.dataset.spaceMobile) : 10;
  const spaceDesktop = slider.dataset.spaceDesktop !== undefined ? Number(slider.dataset.spaceDesktop) : 20;

  new Swiper(slider, {
    slidesPerView: Number(slider.dataset.mobile),
    spaceBetween: spaceMobile,

    speed: Number(slider.dataset.transitionSpeed) || 300,

    loop: slider.dataset.loop === "true",

    centeredSlides: slider.dataset.centered === "true",
    // initial_slide is a hidden (visible_if) setting when centering is off, but its
    // stored value persists, so only honor it while centered slides is actually on.
    initialSlide: slider.dataset.centered === "true" ? Number(slider.dataset.initialSlide) || 0 : 0,

    pagination: {
      el: pagination,
      clickable: true,
    },

    navigation:
      nextBtn && prevBtn
        ? {
            nextEl: nextBtn,
            prevEl: prevBtn,
          }
        : {},

    autoplay:
      slider.dataset.autoplay === "true"
        ? {
            delay: Number(slider.dataset.speed) || 5000,
            disableOnInteraction: false,
          }
        : false,

    breakpoints: {
      750: {
        slidesPerView: Number(slider.dataset.desktop),
        spaceBetween: spaceDesktop,
      },
    },
  });
});


// ingredient_popup drawer code
(() => {
  if (!document.body.classList.contains("product")) return;

  const drawer = document.querySelector(".ingredient_popup");

  if (!drawer) return;

  const openBtn = document.querySelector(".ingredient_icon");
  const closeBtn = drawer.querySelector(".close_btn");
  const overlay = drawer.querySelector(".side_drawer_overlay");
  const sideDrawer = drawer.querySelector(".side_drawer");

  const openDrawer = () => drawer.classList.add("drawer_enabled");
  const closeDrawer = () => drawer.classList.remove("drawer_enabled");

  openBtn?.addEventListener("click", openDrawer);
  closeBtn?.addEventListener("click", closeDrawer);

  overlay?.addEventListener("click", (e) => {
    if (!sideDrawer.contains(e.target)) {
      closeDrawer();
    }
  });
})();

// tabby installment price update
(() => {
  if (!document.body.classList.contains("product")) return;

  function updateInstallmentPrices() {
    const el = document.querySelector(".product .price-item--sale.price-item--last");

    if (!el) {
      console.error("Element not found");
      return;
    }

    const priceText = el.textContent.trim().replace("AED", "").trim();
    const price = parseFloat(priceText);

    if (isNaN(price)) {
      console.error("Error parsing price");
      return;
    }

    const installmentPrice = price / 4;
    document.querySelectorAll(".installment-price").forEach((e) => {
      e.innerHTML = "AED " + installmentPrice.toFixed(2);
    });
  }

  updateInstallmentPrices();

  subscribe(PUB_SUB_EVENTS.variantChange, updateInstallmentPrices);
})();

// newsletter popup (opened from the header "sign-up-popup" button)
(() => {
  const modal = document.querySelector('.newsletter-popup-modal');
  if (!modal) return;

  const openBtn = document.getElementById('sign-up-popup');
  openBtn?.addEventListener('click', () => modal.show(openBtn));

  // Re-open automatically after a full-page postback so the customer sees
  // their submission's success/error message instead of a closed popup.
  if (modal.querySelector('.form__message')) {
    modal.show(openBtn);
  }
})();




function swymCallbackFn(swat) {

    // Re-scan whenever new products are added to the DOM afterwards
    // (infinite scroll, filtering, sorting, etc).
    document.addEventListener("swym:collections-loaded", function() {
        swat.initializeActionButtons("body");
    });
}

if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
}

window.SwymCallbacks.push(swymCallbackFn);

/**
 * Parallax section (sections/parallax.liquid).
 * Same script the section used to run inline on its own <script> tag,
 * unchanged line-for-line — just looped over every "[data-parallax-section]"
 * on the page (instead of a single getElementById) so multiple instances of
 * the section each get their own scroll/resize handlers and state.
 */
document.querySelectorAll('[data-parallax-section]').forEach(function (section) {
  if (window.innerWidth <= 989) return;

  function updateOpacity() {
    const rect = section.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;

    // Visible height of the section inside the viewport
    const visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
    const visibleRatioViewport = Math.max(0, Math.min(visible / vh, 1)); // 0..1 of viewport occupied by section

    // Map: 0→1 by 50% viewport, clamp thereafter; fade out symmetrically when leaving
    const opacity = Math.min(1, visibleRatioViewport / 0.5);

    section.style.opacity = opacity;
  }

  let ticking = false;
  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        updateOpacity();
        ticking = false;
      });
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', updateOpacity);
  updateOpacity(); // initial
});

document.querySelectorAll('[data-parallax-section]').forEach(function (section) {
  if (window.innerWidth <= 989) return;
  const texts = section.querySelectorAll(".text-parallex");
  const images = section.querySelectorAll(".image-parallex");
  const vh = window.innerHeight;

  // ✅ Show first block by default
  if (texts[0]) texts[0].style.opacity = 1;
  if (images[0]) images[0].style.opacity = 1;

  function updateBlocks() {
    const rect = section.getBoundingClientRect();

    if (rect.top <= 0 && rect.bottom >= vh) {
      // 👉 Section is active at top: run fade logic
      texts.forEach((text, i) => {
        const textRect = text.getBoundingClientRect();
        const textCenter = textRect.top + textRect.height / 2;
        const distanceFromCenter = Math.abs(vh / 2 - textCenter);

        let opacity = 1 - Math.min(distanceFromCenter / (vh / 2), 1);

        text.style.opacity = opacity;

        if (images[i]) {
          images[i].style.opacity = opacity;
          images[i].style.zIndex = Math.round(opacity * 100);
        }
      });

    } else if (rect.top > 0) {
      // 👉 Before section hits top: keep only first block visible
      texts.forEach((text, i) => (text.style.opacity = i === 0 ? 1 : 0));
      images.forEach((img, i) => (img.style.opacity = i === 0 ? 1 : 0));

    } else if (rect.bottom < vh) {
      // 👉 After section leaves bottom: keep only last block visible
      const lastIndex = texts.length - 1;
      texts.forEach((text, i) => (text.style.opacity = i === lastIndex ? 1 : 0));
      images.forEach((img, i) => (img.style.opacity = i === lastIndex ? 1 : 0));
    }
  }

  let ticking = false;
  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        updateBlocks();
        ticking = false;
      });
      ticking = true;
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", updateBlocks);
  updateBlocks(); // initial
});